import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaFacebookF,
  FaTwitter,
  FaLinkedinIn,
  FaInstagram,
  FaBriefcase,
} from "react-icons/fa";

const COLUMNS = [
  {
    title: "For Job Seekers",
    links: [
      { label: "Find Jobs", to: "/jobs" },
      { label: "Fresher Jobs", to: "/jobs-for-freshers" },
      { label: "Remote Jobs", to: "/remote-jobs" },
      { label: "Career Resources", to: "/career-resources" },
      { label: "Resume Tips", to: "/resume-tips" },
    ],
  },
  {
    title: "For Employers",
    links: [
      { label: "Post a Job", to: "/employer/jobs" },
      { label: "Employer Registration", to: "/register" },
      { label: "Pricing", to: "/pricing" },
      { label: "Enterprise Hiring", to: "/pricing" },
      { label: "Employer Support", to: "/contact-us" },
    ],
  },
  {
    title: "Explore",
    links: [
      { label: "Jobs by Location", to: "/jobs-by-location" },
      { label: "Jobs by Industry", to: "/jobs-by-industry" },
      { label: "Jobs by Experience", to: "/jobs-by-experience" },
      { label: "Popular Job Titles", to: "/popular-job-titles" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About Us", to: "/about-us" },
      { label: "Contact Us", to: "/contact-us" },
      { label: "Privacy Policy", to: "/privacy-policy" },
      { label: "Terms & Conditions", to: "/terms" },
      { label: "Refund & Cancellation Policy", to: "/refund-policy" },
      { label: "Employer Guidelines", to: "/employer-guidelines" },
      { label: "Candidate Guidelines", to: "/candidate-guidelines" },
    ],
  },
];

const SOCIALS = [
  { icon: <FaFacebookF />, label: "Facebook" },
  { icon: <FaTwitter />, label: "Twitter" },
  { icon: <FaLinkedinIn />, label: "LinkedIn" },
  { icon: <FaInstagram />, label: "Instagram" },
];

function Footer() {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!("IntersectionObserver" in window)) {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true);
          io.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const d = (ms) => ({ transitionDelay: `${ms}ms` });

  return (
    <footer
      ref={ref}
      className={`ft-root relative bg-[#17142A] text-white mt-16 ${shown ? "ft-in" : ""}`}
    >
      <style>{`
        .ft-line { height: 3px; background: linear-gradient(90deg,#7C3AED,#DB2777,#C7F464,#7C3AED); background-size: 300% 100%; animation: ft-slide 6s linear infinite; }
        @keyframes ft-slide { to { background-position: 300% 0; } }
        @keyframes ft-float { 0%,100% { transform: translateY(0) rotate(0); } 50% { transform: translateY(-5px) rotate(-6deg); } }

        .ft-item { opacity: 0; transform: translateY(24px); transition: opacity .7s cubic-bezier(.2,.8,.2,1), transform .7s cubic-bezier(.2,.8,.2,1); }
        .ft-in .ft-item { opacity: 1; transform: none; }

        .ft-logo { animation: ft-float 4s ease-in-out infinite; }

        .ft-link { position: relative; display: block; width: fit-content; transition: transform .25s ease, color .25s ease; }
        .ft-link::after { content: ''; position: absolute; left: 0; bottom: -2px; width: 100%; height: 1px; background: #C7F464; transform: scaleX(0); transform-origin: left; transition: transform .3s ease; }
        .ft-link:hover { transform: translateX(6px); }
        .ft-link:hover::after { transform: scaleX(1); }

        .ft-social { transition: transform .3s ease, background-color .3s ease, box-shadow .3s ease; }
        .ft-social:hover { transform: translateY(-5px) rotate(-8deg) scale(1.1); box-shadow: 0 10px 20px -6px rgba(112,71,255,.7); }

        @media (prefers-reduced-motion: reduce) {
          .ft-line, .ft-logo { animation: none; }
          .ft-item { opacity: 1; transform: none; transition: none; }
          .ft-link, .ft-link::after, .ft-social { transition: none; }
        }
      `}</style>

      {/* Animated gradient line on top */}
      <div className="ft-line" aria-hidden="true" />

      {/* Top Section */}
      <div className="max-w-7xl mx-auto px-6 py-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">

        {/* Logo + Social */}
        <div className="ft-item" style={d(0)}>
          <div className="flex items-center gap-2 mb-4">
            <FaBriefcase className="ft-logo text-3xl text-[#C7F464]" />
            <h2 className="text-3xl font-bold">
              <span className="text-[#C7F464]">PlanB</span>Jobs
            </h2>
          </div>

          <p className="text-gray-300 leading-7">
            PlanB Job helps job seekers find their dream jobs and
            enables employers to hire the right talent quickly.
          </p>

          <div className="flex gap-3 mt-6">
            {SOCIALS.map((s) => (
              <a
                key={s.label}
                href="#"
                aria-label={s.label}
                className="ft-social bg-[#7047FF] p-3 rounded-full hover:bg-[#5B35E0]"
              >
                {s.icon}
              </a>
            ))}
          </div>
        </div>

        {/* Link columns */}
        {COLUMNS.map((col, i) => (
          <div key={col.title} className="ft-item" style={d((i + 1) * 120)}>
            <h3 className="font-bold text-lg mb-4">{col.title}</h3>
            <div className="space-y-3 text-gray-300">
              {col.links.map((l) => (
                <Link key={l.label} to={l.to} className="ft-link hover:text-[#C7F464]">
                  {l.label}
                </Link>
              ))}
            </div>
          </div>
        ))}

      </div>

      {/* Bottom */}
      <div className="border-t border-gray-700">
        <div
          className="ft-item max-w-7xl mx-auto px-6 py-5 flex flex-col md:flex-row justify-between items-center"
          style={d(700)}
        >
          <p className="text-gray-400 text-sm">
            © {new Date().getFullYear()} PlanB Job. All Rights Reserved.
          </p>

          <div className="flex gap-6 mt-3 md:mt-0 text-sm">
            <Link to="/account/delete" className="ft-link hover:text-[#C7F464]">
              Delete Account
            </Link>
          </div>
        </div>
      </div>

    </footer>
  );
}

export default Footer;